import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';
import { Card, Col, message, Row, Table } from 'antd';
import SubnavBar from '../subnav_bar/SubnavBar';
import { assetsRestCrudThunksForBuyer } from '../../thunks/assets_thunks';
import { SUB_ASSETS_TARGET_COLLECTION } from '../../reducers/assets_reducer';
import { nullSafeGet } from '../../utils/DataAccessUtils';
import { ROLE_TYPES } from '../../utils/DataConstants';
import AssetWithQRCodeDisplay from '../asset_row_display/AssetWithQRCodeDisplay';
import { EmptyState } from '../empty_state/EmptyState';
import { getBuyerCompanySettingValue, PERMISSION_NAMES } from '../../utils/AuthUtils';
import AccessPermissionChecker from '../common/AccessPermissionChecker';
import OWButtonWithSupplierManagedLock from '../common/OWButtonWithSupplierManagedLock';
import { getLinkWIthBackLinkParams } from '../../utils/HistoryUtils';

const AssetsDetailSubAssetsPage: FC<any> = ({
	history,
	location,
	match,
	companyConfig,
	fetchSubAssets,
}) => {
	const assetId = match.params.id;
	const [subAssets, setSubAssets] = useState([]);
	const [fetching, setFetching] = useState(true);

	// React Router reuses this component when only :id changes (e.g. navigating
	// from one asset's Sub Assets tab to another), so refetch whenever assetId
	// changes — not just on mount.
	useEffect(() => {
		let cancelled = false;
		setFetching(true);
		fetchSubAssets(assetId)
			.then((records = []) => {
				if (!cancelled) setSubAssets(records);
			})
			.catch((err) => {
				if (!cancelled) {
					setSubAssets([]);
					message.error(err);
				}
			})
			.finally(() => {
				if (!cancelled) setFetching(false);
			});
		return () => {
			cancelled = true;
		};
		// fetchSubAssets intentionally omitted — it's a fresh function each connect
		// render and would refire this effect forever.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [assetId]);

	const allowSubAssets = getBuyerCompanySettingValue(ROLE_TYPES.BUYER)(
		'assetConfig.allowSubAssets',
		companyConfig,
		{},
		false
	);

	const onRow = useCallback(
		(record) => ({
			onClick: () => {
				const path = `/buyer/assets/detail/${nullSafeGet('id', record)}/details`;
				history.push(
					getLinkWIthBackLinkParams(location, 'Back to Asset', path, { preserve: true })
				);
			},
		}),
		[history, location]
	);

	const columns = useMemo(
		() => [
			{
				title: 'Name',
				dataIndex: 'name',
				render: (text, record) => <AssetWithQRCodeDisplay asset={record} />,
			},
		],
		[]
	);

	return (
		<div className="flex flex-col" style={{ padding: '0 0.5em' }}>
			{allowSubAssets ? (
				<AccessPermissionChecker name={PERMISSION_NAMES.MODIFY_ASSETS}>
					<Row style={{ margin: '0 -8px' }} gutter={16}>
						<Col span={24}>
							<SubnavBar
								left={null}
								right={
									<div className="mb-2 flex flex-row items-center justify-end">
										<OWButtonWithSupplierManagedLock
											size="large"
											type="primary"
											onClick={() =>
												history.push(
													// Carry this asset's back-link context (which chains back to the
													// assets index + filters) into the new-asset form so the created
													// sub asset's back button can walk all the way back.
													getLinkWIthBackLinkParams(
														location,
														'Back to Asset',
														`/buyer/assets/overview/new?parentId=${assetId}`,
														{ preserve: true }
													)
												)
											}
										>
											Create sub asset
										</OWButtonWithSupplierManagedLock>
									</div>
								}
							/>
						</Col>
					</Row>
				</AccessPermissionChecker>
			) : null}
			<Card bodyStyle={{ padding: 0 }}>
				<Table
					loading={fetching}
					dataSource={subAssets}
					columns={columns}
					rowKey={(record) => nullSafeGet('id', record)}
					onRow={onRow}
					rowClassName="cursor-pointer"
					showHeader={false}
					pagination={{ hideOnSinglePage: true }}
					locale={{
						emptyText: fetching ? null : (
							<EmptyState
								graphic={
									<img
										alt="No sub assets yet"
										style={{ marginBottom: 8 }}
										src="https://s3.amazonaws.com/mock-data-assets/categories/images/cactus.svg"
									/>
								}
								headline={'No sub assets yet.'}
								body={
									<div style={{ textAlign: 'center' }}>
										<div style={{ maxWidth: 440 }}>
											This asset doesn't have any sub assets associated with it.
										</div>
									</div>
								}
							/>
						),
					}}
				/>
			</Card>
		</div>
	);
};

const mapStateToProps = (state, ownProps) => ({
	history: ownProps.history,
	companyConfig: state.company_config.detail,
});

const mapDispatchToProps = (dispatch) => ({
	fetchSubAssets: (parentId) =>
		dispatch(
			assetsRestCrudThunksForBuyer.read(
				{ parentIds: parentId },
				SUB_ASSETS_TARGET_COLLECTION,
				{ current: 1, pageSize: 1000 },
				{ sort_by: 'name', order: 'ascend' }
			)
		),
});

export default withRouter<any, any>(
	connect(mapStateToProps, mapDispatchToProps)(AssetsDetailSubAssetsPage)
);
